/**
 * Compact "Save to account" button used inside the 3 calculators.
 * Only rendered when the visitor is logged in (JWT token present in
 * localStorage). Clicking POSTs the input+output snapshot to
 * /api/portal/calculator-saves and shows a toast on success.
 *
 * The saved snapshots are visible in the client portal under the
 * "Kalkulátor napló" tab on the Haladás page.
 */
import React, { useState } from 'react';
import { Bookmark, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { portalCalculatorSave } from '../services/api';

const isLoggedIn = () => {
  if (typeof window === 'undefined') return false;
  return !!localStorage.getItem('oc_access_token');
};

export const SaveCalculatorButton = ({ type, input, output, disabled = false, className = '', testid }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const loggedIn = isLoggedIn();

  const handleSave = async () => {
    if (disabled || !input || !output) return;
    setSaving(true);
    try {
      await portalCalculatorSave({ type, input, output });
      setSaved(true);
      toast.success('Elmentve a fiókodba! ✓', { description: 'A Haladás oldalon a "Kalkulátor napló" fülön nézheted meg.' });
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 401) {
        toast.error('Előbb jelentkezz be a fiókodba.');
      } else {
        toast.error(msg || 'Mentés sikertelen. Próbáld újra.');
      }
    } finally { setSaving(false); }
  };

  if (!loggedIn) {
    return (
      <a
        href="/fiokom/bejelentkezes"
        data-testid={testid ? `${testid}-login-cta` : `calc-save-login-cta-${type}`}
        className={`inline-flex items-center gap-2 rounded-md border border-yellow-400/40 bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-300 text-sm font-semibold px-4 h-10 transition-colors ${className}`}
      >
        <Bookmark size={16} /> Jelentkezz be, hogy elmentsd
      </a>
    );
  }

  return (
    <Button
      type="button"
      onClick={handleSave}
      disabled={disabled || saving}
      data-testid={testid || `calc-save-${type}`}
      className={`inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold h-10 px-4 ${className}`}
    >
      {saving ? <Loader2 size={16} className="animate-spin" /> : saved ? <Check size={16} /> : <Bookmark size={16} />}
      {saved ? 'Elmentve' : 'Mentés fiókba'}
    </Button>
  );
};

export default SaveCalculatorButton;
